/**
 * Will It Work Assessment Widget
 * Modal-based compatibility checker for signal boosters.
 *
 * Handles:
 * - Assessment form interaction (signal/coverage selection)
 * - Result calculation and display
 * - Modal view switching (assessment <-> expert)
 * - Add to cart functionality
 */

(function() {
  'use strict';

  /**
   * WillItWorkAssessment class
   * Manages the assessment widget state and interactions
   */
  class WillItWorkAssessment {
    constructor(element) {
      this.element = element;
      this.modalId = element.dataset.modalId;
      this.modal = document.getElementById(this.modalId);

      // State
      this.outdoorSignal = null;
      this.coverageNeed = null;
      this.squareFootage = null;
      this.productData = null;

      // Load product data
      const dataScript = document.getElementById(`wiw-product-data-${this.modalId}`);
      if (dataScript) {
        try {
          this.productData = JSON.parse(dataScript.textContent);
        } catch (e) {
          console.error('WillItWork: Failed to parse product data', e);
        }
      }

      this.bindEvents();
    }

    bindEvents() {
      // Signal option buttons
      this.element.querySelectorAll('[data-wiw-signal]').forEach(btn => {
        btn.addEventListener('click', (e) => this.handleSignalSelect(e.currentTarget.dataset.wiwSignal));
      });

      // No signal link
      const noSignalBtn = this.element.querySelector('[data-wiw-no-signal]');
      if (noSignalBtn) {
        noSignalBtn.addEventListener('click', () => this.handleNoSignal());
      }

      // Coverage option buttons
      this.element.querySelectorAll('[data-wiw-coverage]').forEach(btn => {
        btn.addEventListener('click', (e) => this.handleCoverageSelect(e.currentTarget.dataset.wiwCoverage));
      });

      // Square footage link
      const sqftLink = this.element.querySelector('[data-wiw-sqft-link]');
      if (sqftLink) {
        sqftLink.addEventListener('click', () => this.showSqftBox());
      }

      // Square footage close button
      const sqftClose = this.element.querySelector('[data-wiw-sqft-close]');
      if (sqftClose) {
        sqftClose.addEventListener('click', () => this.hideSqftBox());
      }

      // Square footage input
      const sqftInput = this.element.querySelector('[data-wiw-sqft-input]');
      if (sqftInput) {
        sqftInput.addEventListener('input', (e) => {
          this.squareFootage = e.target.value ? parseInt(e.target.value) : null;
          // Clear coverage selection when entering sqft
          if (this.squareFootage) {
            this.coverageNeed = null;
            this.element.querySelectorAll('[data-wiw-coverage]').forEach(btn => {
              btn.classList.remove('is-selected');
            });
          }
          this.updateCheckButton();
        });
      }

      // Check button
      const checkBtn = this.element.querySelector('[data-wiw-check-btn]');
      if (checkBtn) {
        checkBtn.addEventListener('click', () => this.checkCompatibility());
      }

      // Expert link in form - switch to expert view
      const expertLink = this.element.querySelector('[data-wiw-expert-link]');
      if (expertLink) {
        expertLink.addEventListener('click', () => this.switchToExpertView());
      }
    }

    handleSignalSelect(signal) {
      this.outdoorSignal = signal;
      this.hideResult();

      // Update signal button states
      this.element.querySelectorAll('[data-wiw-signal]').forEach(btn => {
        btn.classList.toggle('is-selected', btn.dataset.wiwSignal === signal);
      });

      // Update step 1 progress
      this.element.querySelector('[data-wiw-progress-1]')?.classList.add('is-complete');
      const stepNumber1 = this.element.querySelector('[data-wiw-step-number-1]');
      if (stepNumber1) {
        stepNumber1.classList.add('is-complete');
        stepNumber1.textContent = '✓';
      }

      // Enable step 2
      this.element.querySelector('[data-wiw-step-2]')?.classList.remove('is-disabled');
      const stepNumber2 = this.element.querySelector('[data-wiw-step-number-2]');
      if (stepNumber2) {
        stepNumber2.classList.remove('is-pending');
      }

      // Show sqft link
      const sqftLink = this.element.querySelector('[data-wiw-sqft-link]');
      if (sqftLink) sqftLink.style.display = 'inline-block';

      this.updateCheckButton();
    }

    handleNoSignal() {
      this.outdoorSignal = 'none';
      this.coverageNeed = null;
      this.squareFootage = null;

      // Show result immediately
      this.showResult();
    }

    handleCoverageSelect(coverage) {
      this.coverageNeed = coverage;
      this.squareFootage = null;
      this.hideResult();

      // Clear sqft input
      const sqftInput = this.element.querySelector('[data-wiw-sqft-input]');
      if (sqftInput) sqftInput.value = '';
      this.hideSqftBox();

      // Update coverage button states
      this.element.querySelectorAll('[data-wiw-coverage]').forEach(btn => {
        btn.classList.toggle('is-selected', btn.dataset.wiwCoverage === coverage);
      });

      // Update step 2 progress
      this.element.querySelector('[data-wiw-progress-2]')?.classList.add('is-complete');
      const stepNumber2 = this.element.querySelector('[data-wiw-step-number-2]');
      if (stepNumber2) {
        stepNumber2.classList.add('is-complete');
        stepNumber2.textContent = '✓';
      }

      this.updateCheckButton();
    }

    showSqftBox() {
      const sqftLink = this.element.querySelector('[data-wiw-sqft-link]');
      const sqftBox = this.element.querySelector('[data-wiw-sqft-box]');

      if (sqftLink) sqftLink.style.display = 'none';
      if (sqftBox) sqftBox.style.display = 'block';

      // Clear coverage selection
      this.coverageNeed = null;
      this.element.querySelectorAll('[data-wiw-coverage]').forEach(btn => {
        btn.classList.remove('is-selected');
      });

      // Reset step 2 progress indicator
      this.element.querySelector('[data-wiw-progress-2]')?.classList.remove('is-complete');
      const stepNumber2 = this.element.querySelector('[data-wiw-step-number-2]');
      if (stepNumber2) {
        stepNumber2.classList.remove('is-complete');
        stepNumber2.textContent = '2';
      }

      this.element.querySelector('[data-wiw-sqft-input]')?.focus();
      this.hideResult();
    }

    hideSqftBox() {
      const sqftLink = this.element.querySelector('[data-wiw-sqft-link]');
      const sqftBox = this.element.querySelector('[data-wiw-sqft-box]');
      const sqftInput = this.element.querySelector('[data-wiw-sqft-input]');

      if (sqftLink) sqftLink.style.display = 'inline-block';
      if (sqftBox) sqftBox.style.display = 'none';
      if (sqftInput) sqftInput.value = '';

      this.squareFootage = null;
      this.updateCheckButton();
    }

    updateCheckButton() {
      const checkBtn = this.element.querySelector('[data-wiw-check-btn]');
      if (checkBtn) {
        const isReady = this.outdoorSignal && this.outdoorSignal !== 'none' && (this.coverageNeed || this.squareFootage);
        checkBtn.disabled = !isReady;
      }
    }

    getCoverageLimit() {
      if (!this.productData || !this.outdoorSignal) return 0;
      switch (this.outdoorSignal) {
        case 'strong': return this.productData.coverage.strong;
        case 'moderate': return this.productData.coverage.moderate;
        case 'weak': return this.productData.coverage.weak;
        default: return 0;
      }
    }

    getCoverageLimitFormatted() {
      return this.getCoverageLimit().toLocaleString();
    }

    // Coverage need thresholds in square feet
    getCoverageThreshold() {
      switch (this.coverageNeed) {
        case 'one-room': return 250;
        case 'several-rooms': return 1000;
        case 'whole-home': return 2000;
        default: return 0;
      }
    }

    // Calculate coverage ratio (product coverage / threshold needed)
    getCoverageRatio() {
      const coverage = this.getCoverageLimit();
      const threshold = this.getCoverageThreshold();
      if (threshold === 0) return 0;
      return coverage / threshold;
    }

    getResult() {
      const upsellUrl = this.productData?.upgradeProduct?.url || null;

      // NO SIGNAL case
      if (this.outdoorSignal === 'none') {
        return {
          type: 'warning',
          title: 'Signal boosters need outdoor signal to amplify',
          message: 'Try checking signal in a few different spots around your property. Sometimes one side of the house or higher ground has usable signal. If you can find at least 1 bar somewhere outside, a booster can help.',
          showUpsell: false,
          showExpert: true,
          showAddToCart: false
        };
      }

      // WITH SQUARE FOOTAGE ENTERED - use specific comparison
      if (this.squareFootage) {
        const sqft = this.squareFootage;
        const coverage = this.getCoverageLimit();
        const ratio = coverage / sqft;

        if (ratio >= 1) {
          // Coverage meets or exceeds their square footage
          return {
            type: 'success',
            title: 'This booster is a great fit for what you need',
            message: `At ${sqft.toLocaleString()} sq ft, you're within the ${this.getCoverageLimitFormatted()} sq ft coverage range for your signal strength.`,
            showUpsell: false,
            showExpert: false,
            showAddToCart: true
          };
        } else if (ratio >= 0.8) {
          // 80-99% - soft nudge
          return {
            type: 'success',
            title: 'This booster should work well for your situation',
            message: `At ${sqft.toLocaleString()} sq ft, you're near the ${this.getCoverageLimitFormatted()} sq ft coverage limit for your signal strength. Want to make sure? Our signal experts can confirm it's the right fit.`,
            showUpsell: false,
            showExpert: true,
            showAddToCart: true
          };
        } else {
          // Below 80% - caution with upgrade (if available)
          const hasUpgrade = !!upsellUrl;
          return {
            type: 'caution',
            title: 'This booster may not be enough for your needs',
            message: hasUpgrade
              ? `At ${sqft.toLocaleString()} sq ft, you're above the ${this.getCoverageLimitFormatted()} sq ft coverage range for your signal strength. We have a more powerful option that would be a better fit.`
              : `At ${sqft.toLocaleString()} sq ft, you're above the ${this.getCoverageLimitFormatted()} sq ft coverage range for your signal strength. We recommend talking with a signal expert to find the right solution.`,
            showUpsell: hasUpgrade,
            upsellUrl,
            showExpert: true,
            showAddToCart: true
          };
        }
      }

      // PRESET OPTIONS (one-room, several-rooms, whole-home)
      const ratio = this.getCoverageRatio();

      if (ratio >= 1) {
        // Coverage meets or exceeds threshold - Success
        return {
          type: 'success',
          title: 'This booster is a great fit for what you need',
          message: 'Based on your signal strength and coverage needs, this booster will provide reliable coverage for your space.',
          showUpsell: false,
          showExpert: false,
          showAddToCart: true
        };
      } else if (ratio >= 0.8) {
        // 80-99% of threshold - Soft nudge
        return {
          type: 'success',
          title: 'This booster should work well for your situation',
          message: 'Based on your signal strength and coverage needs, this booster should provide good coverage. Want to make sure? Our signal experts can confirm it\'s the right fit.',
          showUpsell: false,
          showExpert: true,
          showAddToCart: true
        };
      } else {
        // Below 80% - Caution with upgrade (if available)
        const hasUpgrade = !!upsellUrl;
        return {
          type: 'caution',
          title: 'This booster may not be enough for your needs',
          message: hasUpgrade
            ? 'Based on your signal strength and coverage needs, we have a more powerful option that would be a better fit.'
            : 'Based on your signal strength and coverage needs, we recommend talking with a signal expert to find the right solution.',
          showUpsell: hasUpgrade,
          upsellUrl,
          showExpert: true,
          showAddToCart: true
        };
      }
    }

    checkCompatibility() {
      if (!this.outdoorSignal || (!this.coverageNeed && !this.squareFootage)) return;
      this.showResult();
    }

    showResult() {
      const result = this.getResult();
      const resultEl = this.element.querySelector('[data-wiw-result]');
      const formEl = this.element.querySelector('[data-wiw-form]');

      if (!resultEl) return;

      // Hide form, show result
      if (formEl) formEl.style.display = 'none';
      resultEl.style.display = 'block';

      // Build result HTML
      let resultClass = 'wiw-assessment__result-box--success';
      let iconSymbol = '✓';
      if (result.type === 'caution') {
        resultClass = 'wiw-assessment__result-box--caution';
        iconSymbol = '!';
      } else if (result.type === 'warning') {
        resultClass = 'wiw-assessment__result-box--warning';
        iconSymbol = '!';
      }

      let trustHtml = '';
      if (result.showAddToCart) {
        trustHtml = `
          <div class="wiw-assessment__trust">
            <div class="wiw-assessment__trust-item">
              <div class="wiw-assessment__trust-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <div class="wiw-assessment__trust-text">
                <span class="wiw-assessment__trust-title">All US Carriers</span>
                <span class="wiw-assessment__trust-subtitle">Works with Every Network</span>
              </div>
            </div>
            <div class="wiw-assessment__trust-item">
              <div class="wiw-assessment__trust-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" fill="currentColor"><path d="M98 50c0 26.467-21.533 48-48 48S2 76.467 2 50c0-1.658 1.342-3 3-3s3 1.342 3 3c0 23.159 18.841 42 42 42s42-18.841 42-42S73.159 8 50 8c-11.163 0-21.526 4.339-29.322 12H32c1.658 0 3 1.342 3 3s-1.342 3-3 3H14c-1.658 0-3-1.342-3-3V5c0-1.658 1.342-3 3-3s3 1.342 3 3v10.234C25.851 6.786 37.481 2 50 2c26.467 0 48 21.533 48 48zM77 38v27c0 1.251-.776 2.37-1.945 2.81l-24 9a3.04 3.04 0 0 1-2.11 0l-24-9A3.003 3.003 0 0 1 23 65V38c0-1.251.776-2.37 1.945-2.81l24-9a3.036 3.036 0 0 1 2.109 0l24 9A3.002 3.002 0 0 1 77 38zm-42.457 0L50 43.795 65.457 38 50 32.205zM29 62.92l18 6.75V49.08l-18-6.75zm42 0V42.33l-18 6.75v20.59z"/></svg>
              </div>
              <div class="wiw-assessment__trust-text">
                <span class="wiw-assessment__trust-title">90-Day Returns</span>
                <span class="wiw-assessment__trust-subtitle">No Questions Asked</span>
              </div>
            </div>
            <div class="wiw-assessment__trust-item">
              <div class="wiw-assessment__trust-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path d="M307.66 465.229c0-17.696-14.346-32.042-32.042-32.042h-39.236a32.042 32.042 0 0 0-32.042 32.042c0 17.696 14.346 32.042 32.042 32.042h39.236c17.696 0 32.042-14.346 32.042-32.042zM67.361 382.606c5.352.409 11.827.644 18.331.263a90.96 90.96 0 0 0 89.238 73.36h12.226a50.247 50.247 0 0 0-.008 18H174.93a108.957 108.957 0 0 1-107.569-91.623zm-9.156-19.153A69.812 69.812 0 0 1 0 294.613V252.07a69.812 69.812 0 0 1 69.811-69.812h4.647C81.886 88.502 160.328 14.729 256 14.729s174.114 73.773 181.542 167.529h4.647A69.812 69.812 0 0 1 512 252.07v42.543a69.812 69.812 0 0 1-69.811 69.812h-20.601c-8.544 0-15.47-6.927-15.47-15.47V196.847c0-82.908-67.21-150.118-150.118-150.118s-150.118 67.21-150.118 150.118v152.108c0 6.032-3.453 11.259-8.491 13.81-13.868 4.812-35.375 1.331-39.186.688z"/></svg>
              </div>
              <div class="wiw-assessment__trust-text">
                <span class="wiw-assessment__trust-title">Lifetime Support</span>
                <span class="wiw-assessment__trust-subtitle">US-Based Experts</span>
              </div>
            </div>
          </div>
        `;
      }

      let actionsHtml = '';

      if (result.showUpsell && result.upsellUrl) {
        // Caution with upgrade available: Upgrade (primary), Expert (secondary), Add anyway (tertiary)
        actionsHtml += `<a href="${result.upsellUrl}" class="wiw-assessment__btn wiw-assessment__btn--primary">View Recommended Booster</a>`;
        actionsHtml += `<button type="button" class="wiw-assessment__btn wiw-assessment__btn--secondary" data-wiw-result-expert>Talk with a Signal Expert</button>`;
        actionsHtml += `<button type="button" class="wiw-assessment__btn wiw-assessment__btn--tertiary" data-wiw-add-to-cart>Add This Booster Anyway</button>`;
      } else if (result.showAddToCart && result.showExpert) {
        // Soft nudge: Add to Cart (primary), Expert (secondary)
        actionsHtml += `<button type="button" class="wiw-assessment__btn wiw-assessment__btn--primary" data-wiw-add-to-cart>Add to Cart</button>`;
        actionsHtml += `<button type="button" class="wiw-assessment__btn wiw-assessment__btn--secondary" data-wiw-result-expert>Talk with a Signal Expert</button>`;
      } else if (result.showAddToCart) {
        // Success: Add to Cart only
        actionsHtml += `<button type="button" class="wiw-assessment__btn wiw-assessment__btn--primary" data-wiw-add-to-cart>Add to Cart</button>`;
      } else if (result.showExpert) {
        // No signal / expert only
        actionsHtml += `<button type="button" class="wiw-assessment__btn wiw-assessment__btn--secondary" data-wiw-result-expert>Talk to a Signal Expert</button>`;
      }

      actionsHtml += `<button type="button" class="wiw-assessment__start-over" data-wiw-start-over>← Start over</button>`;

      resultEl.innerHTML = `
        <div class="wiw-assessment__result-box ${resultClass}">
          <div class="wiw-assessment__result-header">
            <div class="wiw-assessment__result-icon">${iconSymbol}</div>
            <h4 class="wiw-assessment__result-title">${result.title}</h4>
          </div>
          <p class="wiw-assessment__result-message">${result.message}</p>
        </div>
        ${trustHtml}
        <div class="wiw-assessment__actions">
          ${actionsHtml}
        </div>
      `;

      this.bindResultEvents();
    }

    hideResult() {
      const resultEl = this.element.querySelector('[data-wiw-result]');
      const formEl = this.element.querySelector('[data-wiw-form]');
      if (resultEl) resultEl.style.display = 'none';
      if (formEl) formEl.style.display = 'block';
    }

    bindResultEvents() {
      // Add to cart button
      this.element.querySelectorAll('[data-wiw-add-to-cart]').forEach(btn => {
        btn.addEventListener('click', () => this.handleAddToCart());
      });

      // Expert button in results
      this.element.querySelectorAll('[data-wiw-result-expert]').forEach(btn => {
        btn.addEventListener('click', () => this.switchToExpertView());
      });

      // Start over button
      this.element.querySelectorAll('[data-wiw-start-over]').forEach(btn => {
        btn.addEventListener('click', () => this.handleStartOver());
      });
    }

    handleStartOver() {
      this.outdoorSignal = null;
      this.coverageNeed = null;
      this.squareFootage = null;

      // Reset signal buttons
      this.element.querySelectorAll('[data-wiw-signal]').forEach(btn => {
        btn.classList.remove('is-selected');
      });

      // Reset coverage buttons
      this.element.querySelectorAll('[data-wiw-coverage]').forEach(btn => {
        btn.classList.remove('is-selected');
      });

      // Reset step 1
      this.element.querySelector('[data-wiw-progress-1]')?.classList.remove('is-complete');
      const stepNumber1 = this.element.querySelector('[data-wiw-step-number-1]');
      if (stepNumber1) {
        stepNumber1.classList.remove('is-complete');
        stepNumber1.textContent = '1';
      }

      // Reset step 2
      this.element.querySelector('[data-wiw-progress-2]')?.classList.remove('is-complete');
      this.element.querySelector('[data-wiw-step-2]')?.classList.add('is-disabled');
      const stepNumber2 = this.element.querySelector('[data-wiw-step-number-2]');
      if (stepNumber2) {
        stepNumber2.classList.remove('is-complete');
        stepNumber2.classList.add('is-pending');
        stepNumber2.textContent = '2';
      }

      // Hide sqft input
      const sqftLink = this.element.querySelector('[data-wiw-sqft-link]');
      if (sqftLink) sqftLink.style.display = 'none';
      this.element.querySelector('[data-wiw-sqft-box]').style.display = 'none';
      const sqftInput = this.element.querySelector('[data-wiw-sqft-input]');
      if (sqftInput) sqftInput.value = '';

      // Update check button
      this.updateCheckButton();

      // Hide result, show form
      this.hideResult();
    }

    switchToExpertView() {
      if (!this.modal) return;

      // Switch views
      const assessmentView = this.modal.querySelector('[data-modal-view="assessment"]');
      const expertView = this.modal.querySelector('[data-modal-view="expert"]');

      if (assessmentView) assessmentView.classList.remove('is-active');
      if (expertView) expertView.classList.add('is-active');

      // Show back button and add header padding so it doesn't overlap the title
      const backBtn = this.modal.querySelector('[data-modal-back]');
      if (backBtn) backBtn.style.display = 'flex';

      const header = this.modal.querySelector('[data-modal-header]');
      if (header) header.classList.add('scta-modal__header--has-back');
    }

    async handleAddToCart() {
      const variantId = this.productData?.variantId;
      if (!variantId) return;

      const addBtn = this.element.querySelector('[data-wiw-add-to-cart]');
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.textContent = 'Adding...';
      }

      try {
        const cartDrawer = document.querySelector('cart-drawer');
        let sections = 'cart-icon-bubble';
        if (cartDrawer) {
          const sectionId = cartDrawer.closest('.shopify-section')?.id?.replace('shopify-section-', '');
          if (sectionId) sections += `,${sectionId}`;
        }

        const formData = new FormData();
        formData.append('id', parseInt(variantId));
        formData.append('quantity', 1);
        formData.append('sections', sections);
        formData.append('sections_url', window.location.pathname);

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.description || 'Failed to add to cart');
        }

        // Update cart icon bubble
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble && data.sections?.['cart-icon-bubble']) {
          cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
        }

        // Close modal
        this.closeModal();

        // Open cart drawer or redirect
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(data, theme?.settings?.afterAtc === 'drawer');
        } else if (theme?.settings?.afterAtc === 'page') {
          window.location.href = '/cart';
        }

      } catch (error) {
        console.error('WillItWork: Cart error', error);
        if (addBtn) {
          addBtn.textContent = 'Add to Cart';
          addBtn.disabled = false;
        }
        alert('Failed to add to cart. Please try again.');
      }
    }

    closeModal() {
      if (this.modal) {
        this.modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      }
    }
  }

  /**
   * Modal Controller
   * Handles opening/closing and view switching for multi-view modals
   */
  class WillItWorkModal {
    constructor(modal) {
      this.modal = modal;
      this.bindEvents();
    }

    bindEvents() {
      // Back button
      const backBtn = this.modal.querySelector('[data-modal-back]');
      if (backBtn) {
        backBtn.addEventListener('click', () => this.switchToAssessmentView());
      }

      // Close button
      const closeBtn = this.modal.querySelector('.scta-modal__close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.close());
      }

      // Backdrop click
      const backdrop = this.modal.querySelector('.scta-modal__backdrop');
      if (backdrop) {
        backdrop.addEventListener('click', () => this.close());
      }

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.getAttribute('aria-hidden') === 'false') {
          this.close();
        }
      });
    }

    open(view = 'expert') {
      // Set active view
      const assessmentView = this.modal.querySelector('[data-modal-view="assessment"]');
      const expertView = this.modal.querySelector('[data-modal-view="expert"]');

      if (assessmentView) assessmentView.classList.toggle('is-active', view === 'assessment');
      if (expertView) expertView.classList.toggle('is-active', view === 'expert');

      // Show/hide back button based on view
      const backBtn = this.modal.querySelector('[data-modal-back]');
      if (backBtn) {
        backBtn.style.display = view === 'expert' ? 'none' : 'none'; // Back button only shows when navigating FROM assessment
      }

      // Open modal
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    switchToAssessmentView() {
      const assessmentView = this.modal.querySelector('[data-modal-view="assessment"]');
      const expertView = this.modal.querySelector('[data-modal-view="expert"]');

      if (assessmentView) assessmentView.classList.add('is-active');
      if (expertView) expertView.classList.remove('is-active');

      // Hide back button and remove header padding
      const backBtn = this.modal.querySelector('[data-modal-back]');
      if (backBtn) backBtn.style.display = 'none';

      const header = this.modal.querySelector('[data-modal-header]');
      if (header) header.classList.remove('scta-modal__header--has-back');
    }

    switchToExpertView() {
      const assessmentView = this.modal.querySelector('[data-modal-view="assessment"]');
      const expertView = this.modal.querySelector('[data-modal-view="expert"]');

      if (assessmentView) assessmentView.classList.remove('is-active');
      if (expertView) expertView.classList.add('is-active');

      // Show back button and add header padding
      const backBtn = this.modal.querySelector('[data-modal-back]');
      if (backBtn) backBtn.style.display = 'flex';

      const header = this.modal.querySelector('[data-modal-header]');
      if (header) header.classList.add('scta-modal__header--has-back');
    }
  }

  /**
   * Initialize on DOM ready
   */
  function init() {
    // Initialize all assessment widgets
    document.querySelectorAll('[data-wiw-assessment]').forEach(el => {
      new WillItWorkAssessment(el);
    });

    // Initialize modal controllers
    document.querySelectorAll('.scta-modal[data-wiw-modal]').forEach(modal => {
      const controller = new WillItWorkModal(modal);
      modal._wiwController = controller;
    });

    // Handle trigger clicks (below trust badges)
    document.querySelectorAll('[data-wiw-trigger]').forEach(trigger => {
      trigger.addEventListener('click', () => {
        const modalId = trigger.dataset.modalId;
        const modal = document.getElementById(modalId);
        if (modal && modal._wiwController) {
          modal._wiwController.open('assessment');
        }
      });
    });

    // Handle description link triggers
    document.querySelectorAll('[data-wiw-description-link]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const modalId = link.dataset.modalId;
        const modal = document.getElementById(modalId);
        if (modal && modal._wiwController) {
          modal._wiwController.open('assessment');
        }
      });
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
